export type DashboardTab =
  | "home"
  | "request-log"
  | "audit-log"
  | "compliance"
  | "cache"
  | "preload"
  | "live-users"
  | "recordings"
  | "cookies"
  | "forms"
  | "leads"
  | "redirects"
  | "queue"
  | "site-structure"
  | "ab-tests"
  | "deploy"
  | "billing"
  | "firewall"
  | "settings";

/** Flat registry kept for capability maps / upgrade copy; primary nav uses `nav/navConfig`. */
export const DASHBOARD_TABS: { id: DashboardTab; label: string; badge?: "live" | "leads" | "deploy" }[] = [
  { id: "home", label: "Overview" },
  { id: "request-log", label: "Request log" },
  { id: "audit-log", label: "Audit log" },
  { id: "compliance", label: "Compliance" },
  { id: "cache", label: "Cache" },
  { id: "preload", label: "Preload" },
  { id: "live-users", label: "Live visitors", badge: "live" },
  { id: "recordings", label: "Recordings" },
  { id: "cookies", label: "Cookies" },
  { id: "forms", label: "Forms" },
  { id: "leads", label: "Leads", badge: "leads" },
  { id: "redirects", label: "Redirects" },
  { id: "queue", label: "Origin queue" },
  { id: "site-structure", label: "Pages" },
  { id: "ab-tests", label: "Experiments" },
  { id: "deploy", label: "Deploy", badge: "deploy" },
  { id: "billing", label: "Plan usage" },
  { id: "firewall", label: "Firewall" },
  { id: "settings", label: "Site settings" }
];

export type DashUser = {
  email?: string;
  role?: string;
  isAdministrator?: boolean;
};

export type AbAssignmentDetail = {
  testId: string;
  testName: string;
  flowId: string;
  flowName: string;
  propertyKey?: string;
  /** Index in test.flows — matches FLOW_COLORS in A/B setup UI */
  flowIndex?: number;
};

export type RequestLogEntry = {
  id?: number | string;
  ts?: number;
  method?: string;
  url?: string;
  status?: number;
  source?: string;
  sourceBucket?: string;
  group?: string;
  ip?: string;
  countryCode?: string;
  userAgent?: string;
  durationMs?: number;
  originMs?: number;
  originTtfbMs?: number;
  isBot?: boolean;
  botProbability?: number;
  rocketLoader?: boolean;
  staging?: boolean;
  site?: string;
  host?: string;
  domain?: string;
  userId?: string;
  pageTitle?: string;
  title?: string;
  timeOnPageMs?: number;
  interactions?: number;
  scrollDepthPct?: number;
  engagementAtMs?: number;
  recordingId?: string;
  recordingSeekMs?: number;
  abTests?: AbAssignmentDetail[];
};

export type RequestLogDetail = {
  id?: number | string;
  ts?: number;
  userId?: string;
  method?: string;
  url?: string;
  status?: number;
  durationMs?: number;
  originMs?: number;
  originTtfbMs?: number;
  source?: string;
  sourceBucket?: string;
  host?: string;
  ip?: string;
  countryCode?: string;
  userAgent?: string;
  group?: string;
  isBot?: boolean;
  botProbability?: number;
  rocketLoader?: boolean;
  timeOnPageMs?: number;
  interactions?: number;
  scrollDepthPct?: number;
  engagementAtMs?: number;
  pageTitle?: string;
  title?: string;
  req?: {
    headers?: Record<string, string>;
    query?: Record<string, unknown>;
    body?: string;
    bodyTruncated?: boolean;
  };
  res?: {
    headers?: Record<string, string>;
    body?: string;
    bodyTruncated?: boolean;
    redirectLocation?: string;
  };
  notes?: {
    clientNav?: {
      totalMs?: number;
      dnsMs?: number;
      tlsMs?: number;
      ttfbMs?: number;
    };
    synthetic?: boolean;
  };
  error?: boolean;
  message?: string;
};

export type UserProfile = {
  email?: string;
  name?: string;
  company?: string;
  firstReferrer?: string;
  firstLandingPath?: string;
  firstSeenAtMs?: number;
  updatedAtMs?: number;
  lastLeadId?: string;
  lastLeadAtMs?: number;
  lastLeadGroupId?: string;
};

export type LogEngagement = {
  timeOnPageMs: number;
  interactions: number;
  scrollDepthPct: number;
  updatedAtMs: number;
};

export type AuditEntry = {
  id?: string;
  ts?: number;
  category?: string;
  action?: string;
  severity?: string;
  actorEmail?: string;
  path?: string;
  summary?: string;
};

export type DashboardStatus = Record<string, unknown>;
